<script lang="ts" setup>
import { useRouter } from 'vue-router';
import { marked } from 'marked';
import { useAppStore } from '@/home/stores/app';
import { useAsyncData } from '@/home/hooks/async-data';
import { $fetch } from '@/home/plugins/fetch';
import { statsPv } from '@/home/utils';
import { useI18n } from 'vue-i18n';
import Card from './components/Card.vue';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
});

marked.use({
  gfm: true,
  breaks: true,
});

const router = useRouter();
const appStore = useAppStore();
const { t } = useI18n()

const defaultTitle = t('certification.index.title');

const { data: detail } = useAsyncData<Record<string, any>>(
  'certification:detail',
  () => $fetch('/certification/v1/info', { data: { certificationId: props.id } }),
  {
    cache: false,
    watch: [() => props.id],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (serverContext) {
        if (handleSuccess && detail.value) {
          serverContext.title = `${detail.value.certificationName} - ${defaultTitle}`;
          serverContext.description = detail.value.description;
        }
        else {
          // 如果请求出错直接服务器重定向到 404 页面
          serverContext.redirect = '/404';
        }
      }
      else {
        if (handleSuccess && detail.value) {
          appStore.setTitle(`${detail.value?.certificationName} - ${defaultTitle}`);

          // 统计埋点: 因标题是异步获取的，所以 pv 要延迟统计
          statsPv();
        }
        else {
          router.replace('/404');
        }
      }
    },
  },
);
</script>

<template>
  <main class="page-certification-detail pb-80">
    <section class="banner h-400">
      <div class="container pt-146 text-#2d2d2d">
        <h1 class="mt-0 h-70 max-w-900 truncate text-50 font-medium leading-70 <2xl:max-w-830" :title="detail.certificationName" v-if="detail">
          {{ detail.certificationName }}
        </h1>
      </div>
    </section>

    <template v-if="detail">
      <section class="container mt-48" v-if="detail.frame">
        <h2 class="m-0 text-center text-32 text-#2d2d2d font-medium leading-48">
          {{ $t('certification.detail.title1') }}
        </h2>

        <img :src="detail.frame" class="mx-auto mt-48 block max-w-full" :alt="$t('certification.detail.title1')">
      </section>

      <section class="container mt-48" v-if="detail.courseList?.length">
        <h2 class="m-0 text-center text-32 text-#2d2d2d font-medium leading-48">
          {{ $t('certification.detail.title2') }}
        </h2>

        <div>
          <Card v-for="course in detail.courseList" :key="course.courseId" :title="course.courseName" :link="course.link" class="mt-48">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="marked.parse(course.description)"></div>
          </Card>
        </div>
      </section>

      <section class="container mt-48" v-if="detail.flow">
        <h2 class="m-0 text-center text-32 text-#2d2d2d font-medium leading-48">
          {{ $t('certification.detail.title3') }}
        </h2>

        <img :data-src="detail.flow" class="lazyload mx-auto mt-48 block max-w-full" :alt="$t('certification.detail.title3')">
      </section>

      <section class="container mt-48" v-if="detail.partnerList?.length">
        <h2 class="m-0 text-center text-32 text-#2d2d2d font-medium leading-48">
          {{ $t('certification.detail.title7') }}
        </h2>
        <ul class="contributors m-0 flex flex-shrink flex-wrap list-none p-0">
          <li v-for="item in detail.partnerList" :key="item.partnerId">
            <img class="lazyload" :data-src="item.logo" :alt="item.partnerName" :title="item.partnerName">
          </li>
        </ul>
      </section>

    </template>
  </main>
</template>

<style lang="scss" scoped>
.page-certification-detail {
  * {
    box-sizing: border-box;
  }

  .banner {
    background-color: #f4f6f7;
    background-image: url(@/home/assets/certification/banner.jpg);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 1920px auto;
  }

  .contributors {
    margin-top: 48px;

    li {
      width: 213px;
      height: 78px;
      margin-left: 34px;
      background-color: #fff;

      &:nth-child(n+7) {
        margin-top: 16px;
      }

      &:nth-child(6n+1) {
        margin-left: 0;
      }

      &:hover {
        img {
          width: 110%;
          height: 110%;
          margin-top: -2%;
          margin-left: -5%;
        }
      }

      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.3s;
      }
    }
  }
}
</style>
